import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Actions ledger + outcome checkpoints (A1/A2 measurement layer).
 *
 * An `Action` is a durable record of a user/system event we want to measure the
 * outcome of. Each action can seed one or more `OutcomeCheckpoint` rows that
 * are reaped at +7d / +28d to attach a measured value (SERP position,
 * GSC clicks/impressions, open critical count, ...).
 *
 * `ImpactCard` / `ImpactCardsResponse` are the API-facing rollups the frontend
 * renders on the project home ("what did this action move?").
 */

export const ActionKindSchema = z.enum([
  'brief_exported',
  'refresh_started',
  'keyword_tracked',
  'audit_completed',
  'audit_fix',
]);
export type ActionKind = z.infer<typeof ActionKindSchema>;

export const ActionSubjectTypeSchema = z.enum([
  'brief',
  'tracked_keyword',
  'url',
  'audit',
  'audit_issue',
]);
export type ActionSubjectType = z.infer<typeof ActionSubjectTypeSchema>;

export const OutcomeMetricSchema = z.enum([
  'serp_position',
  'gsc_clicks_impressions',
  'audit_open_criticals',
]);
export type OutcomeMetric = z.infer<typeof OutcomeMetricSchema>;

export const OutcomeCheckpointStatusSchema = z.enum(['pending', 'attached', 'skipped']);
export type OutcomeCheckpointStatus = z.infer<typeof OutcomeCheckpointStatusSchema>;

export const OutcomeCheckpointOffsetDaysSchema = z.union([z.literal(7), z.literal(28)]);
export type OutcomeCheckpointOffsetDays = z.infer<typeof OutcomeCheckpointOffsetDaysSchema>;

export const ActionSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  kind: ActionKindSchema,
  subject_type: ActionSubjectTypeSchema,
  /** UUID or URL string — see subject_type. */
  subject_id: z.string(),
  target_url: z.string().nullable(),
  tracked_keyword_id: UuidSchema.nullable(),
  audit_id: UuidSchema.nullable(),
  baseline: z.record(z.unknown()),
  metadata: z.record(z.unknown()),
  actor_user_id: UuidSchema.nullable(),
  created_at: IsoDateTimeSchema,
});
export type Action = z.infer<typeof ActionSchema>;

export const CreateActionRequestSchema = z.object({
  project_id: UuidSchema,
  kind: ActionKindSchema,
  subject_type: ActionSubjectTypeSchema,
  subject_id: z.string().min(1),
  target_url: z.string().url().nullable().optional(),
  tracked_keyword_id: UuidSchema.nullable().optional(),
  audit_id: UuidSchema.nullable().optional(),
  baseline: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type CreateActionRequest = z.infer<typeof CreateActionRequestSchema>;

export const OutcomeCheckpointSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  action_id: UuidSchema,
  project_id: UuidSchema,
  metric: OutcomeMetricSchema,
  offset_days: OutcomeCheckpointOffsetDaysSchema,
  due_at: IsoDateTimeSchema,
  attached_at: IsoDateTimeSchema.nullable(),
  status: OutcomeCheckpointStatusSchema,
  value: z.record(z.unknown()).nullable(),
  warning: z.string().nullable(),
});
export type OutcomeCheckpoint = z.infer<typeof OutcomeCheckpointSchema>;

/**
 * Signed delta on open-critical count between two consecutive completed audits.
 * `previous` = older completed audit; `current` = newer.  `delta` is
 * `current - previous` so a negative number means we closed criticals.
 */
export const CriticalsDeltaSchema = z.object({
  previous: z.number().int().min(0).nullable(),
  current: z.number().int().min(0).nullable(),
  delta: z.number().int().nullable(),
  previous_audit_id: UuidSchema.nullable(),
  current_audit_id: UuidSchema.nullable(),
});
export type CriticalsDelta = z.infer<typeof CriticalsDeltaSchema>;

/**
 * A6 → A1 back-reference for brief actions. When a brief was created from a
 * calendar item, the impact card carries a slim summary of that item so the
 * UI can render "From calendar: <title>" without a follow-up round-trip.
 * Null when the action's subject is not a brief, or the brief isn't linked
 * to a calendar item.
 */
export const ImpactCardCalendarItemSchema = z.object({
  id: UuidSchema,
  calendar_id: UuidSchema,
  title: z.string(),
});
export type ImpactCardCalendarItem = z.infer<typeof ImpactCardCalendarItemSchema>;

/**
 * API-facing rollup for the project home "Impact" strip.  One card per action
 * with any measured checkpoints attached.  `outcomes` is keyed by
 * `${metric}:${offset_days}` (e.g. `serp_position:7`) so the UI can render
 * side-by-side 7d vs 28d columns without another round-trip.
 */
export const ImpactCardSchema = z.object({
  action: ActionSchema,
  outcomes: z.array(OutcomeCheckpointSchema),
  /**
   * A6 back-reference — populated for `subject_type: 'brief'` actions whose
   * brief has a linked calendar item. Null otherwise. Denormalized here so
   * the UI can render "From calendar" without an N+1 fetch.
   */
  calendar_item: ImpactCardCalendarItemSchema.nullable().default(null),
});
export type ImpactCard = z.infer<typeof ImpactCardSchema>;

export const ImpactCardsResponseSchema = z.object({
  cards: z.array(ImpactCardSchema),
});
export type ImpactCardsResponse = z.infer<typeof ImpactCardsResponseSchema>;
